import classNames from 'classnames';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import SwiperCore, { Navigation, Pagination } from 'swiper';
import { ReactComponent as SvgArrowWhiteBg } from 'public/assets/icons/arrow-white-bg.svg';
import {
  useRef, useCallback, ReactNode, useMemo, useState,
} from 'react';
import { SwiperOptions } from 'swiper/types';

SwiperCore.use([Navigation, Pagination]);

export type SwiperBreakpoints = {
  [width: number]: SwiperOptions;
  [ratio: string]: SwiperOptions;
};

export interface SwiperSlideItem {
  key: string | number;
  content: ReactNode;
}

export interface CustomNavigationProps {
  handlePrev: () => void;
  handleNext: () => void;
  activeIndex: number;
  slidesCount: number;
}

export interface SwiperComponentProps {
  /** Array of slide items with key and content */
  slides: SwiperSlideItem[];
  /** Breakpoints configuration for responsive behavior */
  breakpoints?: SwiperBreakpoints;
  /** Space between slides in pixels */
  spaceBetween?: number;
  /** Number of slides per view or 'auto' */
  slidesPerView?: number | 'auto';
  /** Whether to show navigation buttons */
  showNavigation?: boolean;
  /** Minimum slides count to show navigation (default: uses showNavigation prop) */
  navigationThreshold?: number;
  /** Whether to show pagination dots */
  showPagination?: boolean;
  /** Custom class for the wrapper container */
  wrapperClassName?: string;
  /** Custom class for the slider wrapper (contains buttons and swiper) */
  sliderWrapperClassName?: string;
  /** Custom class for the Swiper component */
  swiperClassName?: string;
  /** Custom class for each slide */
  slideClassName?: string;
  /** Custom class for navigation buttons */
  navButtonClassName?: string;
  /** Custom class for prev button */
  prevButtonClassName?: string;
  /** Custom class for next button */
  nextButtonClassName?: string;
  /** Callback when swiper instance is available */
  onSwiperInit?: (swiper: SwiperCore) => void;
  /** Callback when slide changes */
  onSlideChange?: (swiper: SwiperCore) => void;
  /** Callback when slide change transition ends */
  onSlideChangeTransitionEnd?: (swiper: SwiperCore) => void;
  /** Custom navigation icon component */
  NavigationIcon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  /** Whether to use built-in swiper navigation (for NewsRoomHero style) */
  useBuiltInNavigation?: boolean;
  /** Ref for the swiper container */
  swiperRef?: React.MutableRefObject<SwiperCore | null>;
  /** Additional Swiper options */
  additionalOptions?: Partial<SwiperOptions>;
  /** Custom navigation renderer for special cases (e.g., hide prev on first slide) */
  renderCustomNavigation?: (props: CustomNavigationProps) => ReactNode;
}

export const SwiperComponent = ({
  slides,
  breakpoints,
  spaceBetween = 16,
  slidesPerView = 'auto',
  showNavigation = true,
  navigationThreshold,
  showPagination = false,
  wrapperClassName,
  sliderWrapperClassName,
  swiperClassName,
  slideClassName,
  navButtonClassName,
  prevButtonClassName,
  nextButtonClassName,
  onSwiperInit,
  onSlideChange,
  onSlideChangeTransitionEnd,
  NavigationIcon = SvgArrowWhiteBg,
  useBuiltInNavigation = false,
  swiperRef: externalSwiperRef,
  additionalOptions,
  renderCustomNavigation,
}: SwiperComponentProps) => {
  const internalSwiperRef = useRef<SwiperCore | null>(null);
  const swiperRef = externalSwiperRef || internalSwiperRef;
  const [activeIndex, setActiveIndex] = useState(0);

  const handlePrev = useCallback(() => {
    if (!swiperRef.current) return;
    swiperRef.current.slidePrev();
  }, [swiperRef]);

  const handleNext = useCallback(() => {
    if (!swiperRef.current) return;
    swiperRef.current.slideNext();
  }, [swiperRef]);

  const handleSwiper = useCallback((swiperInstance: SwiperCore) => {
    // eslint-disable-next-line no-param-reassign
    swiperRef.current = swiperInstance;
    onSwiperInit?.(swiperInstance);
  }, [swiperRef, onSwiperInit]);

  const handleSlideChange = useCallback((swiper: SwiperCore) => {
    setActiveIndex(swiper.activeIndex);
    onSlideChange?.(swiper);
  }, [onSlideChange]);

  const handleSlideChangeTransitionEnd = useCallback((swiper: SwiperCore) => {
    setActiveIndex(swiper.activeIndex);
    onSlideChangeTransitionEnd?.(swiper);
  }, [onSlideChangeTransitionEnd]);

  const shouldShowNavigation = useMemo(() => {
    if (!showNavigation) return false;
    if (navigationThreshold !== undefined) {
      return slides.length > navigationThreshold;
    }
    return true;
  }, [showNavigation, navigationThreshold, slides.length]);

  const renderNavigation = () => {
    if (useBuiltInNavigation) return null;

    if (renderCustomNavigation) {
      return renderCustomNavigation({
        handlePrev,
        handleNext,
        activeIndex,
        slidesCount: slides.length,
      });
    }

    if (!shouldShowNavigation) return null;

    return (
      <>
        <button
          type="button"
          className={classNames(navButtonClassName, prevButtonClassName)}
          onClick={handlePrev}
        >
          <NavigationIcon />
        </button>
        <button
          type="button"
          className={classNames(navButtonClassName, nextButtonClassName)}
          onClick={handleNext}
        >
          <NavigationIcon />
        </button>
      </>
    );
  };

  const content = (
    <>
      {renderNavigation()}
      <Swiper
        slidesPerView={slidesPerView}
        breakpoints={breakpoints}
        spaceBetween={spaceBetween}
        className={swiperClassName}
        onSwiper={handleSwiper}
        onSlideChange={handleSlideChange}
        onSlideChangeTransitionEnd={handleSlideChangeTransitionEnd}
        pagination={showPagination}
        navigation={useBuiltInNavigation}
        {...additionalOptions}
      >
        {slides.map((slide) => (
          <SwiperSlide
            key={slide.key}
            className={slideClassName}
          >
            {slide.content}
          </SwiperSlide>
        ))}
      </Swiper>
    </>
  );

  // If both wrapperClassName and sliderWrapperClassName are provided, use nested divs
  if (wrapperClassName && sliderWrapperClassName) {
    return (
      <div className={wrapperClassName}>
        <div className={sliderWrapperClassName}>
          {content}
        </div>
      </div>
    );
  }

  // If only sliderWrapperClassName, use single wrapper
  if (sliderWrapperClassName) {
    return (
      <div className={sliderWrapperClassName}>
        {content}
      </div>
    );
  }

  // If only wrapperClassName, use single wrapper
  if (wrapperClassName) {
    return (
      <div className={wrapperClassName}>
        {content}
      </div>
    );
  }

  return <>{content}</>;
};

export { SwiperSlide } from 'swiper/react';
export type { SwiperCore };
